import { TYRE_IDENTIFIER_FIELD, ITyre } from '@wyz/types';
import { Badge, Col, Flex, Modal, Row, Text, Title } from '@wyz/ui';
import {
  DEFAULT_TIRE_BRAND_LOGO,
  DEFAULT_TIRE_BRAND_WIDTH,
  getBrandLogoUrl,
  BlobHelpers,
} from '@wyz/utils';
import {
  useApplicationContext,
  useConfig,
  useI18n,
  Trans,
} from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { ImageSlide, Img, SeasonIcon } from '~/purchase/components';
import { DEFAULT_ENERGY_LABEL, getFilterTranslation } from '~/purchase/helpers';
import { useEffect, useState } from 'react';

type Props = {
  onClose: () => void;
  customer: IUserCustomer;
  product: ITyre;
};

const getter = {
  eco: (product: ITyre) => product.specification.eco,
  sport: (product: ITyre) => product.specification.sportCompatibility,
  electric: (product: ITyre) => product.specification.electricId,
} as const;

const DetailModal = ({ onClose, product, customer }: Props) => {
  const { t } = useI18n();
  const { cdnUrl, withCdnUrl, apiUrl } = useConfig();
  const { auth } = useApplicationContext();
  const specificities = [];
  const [energyLabelSrc, setEnergyLabelSrc] = useState<string>(
    `data:image/png;base64,${DEFAULT_ENERGY_LABEL}`,
  );

  useEffect(() => {
    const controller = new AbortController();
    BlobHelpers.getDataUrl({
      url: `${apiUrl}/offers/api/assets/tyres/${product.ean}/labels/medium`,
      token: auth.user?.access_token ?? '',
      signal: controller.signal,
    })
      .then((dataUrl) => {
        if (dataUrl) {
          setEnergyLabelSrc(dataUrl);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [apiUrl, product.ean, auth.user?.access_token]);

  for (const specificity of ['eco', 'sport', 'electric'] as const) {
    const value = getter[specificity](product);
    if (value) {
      specificities.push(t(getFilterTranslation(specificity)));
    }
  }

  return (
    <Modal.Container open={true} onClose={onClose} size='xl' centered>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <Row className='ps-2'>
          <Col sm={5}>
            <ImageSlide
              images={[
                {
                  src: product.pictures?.fullView
                    ? `/common/tyres${product.pictures?.fullView}`
                    : undefined,
                  defaultImage: withCdnUrl('/img/tire-sample.png'),
                  baseUrl: cdnUrl,
                },
                {
                  src: energyLabelSrc,
                  defaultImage: `data:image/png;base64,${DEFAULT_ENERGY_LABEL}`,
                  baseUrl: undefined,
                },
              ]}
            />
          </Col>
          <Col>
            <Img
              src={getBrandLogoUrl(product.brand.name)}
              baseUrl={cdnUrl}
              defaultImage={DEFAULT_TIRE_BRAND_LOGO}
              alt={product.brand.name}
              className='mb-2'
              width={DEFAULT_TIRE_BRAND_WIDTH.lg}
              style={{ objectFit: 'scale-down' }}
            />
            <Title
              heading='lg'
              color='tertiary'
              weight='bold'
              className='text-uppercase'
            >
              {product.brand.name} {product.profile.design1}
            </Title>
            <Flex className='gap-2 mt-2' wrap='wrap' alignItems='center'>
              <Badge pill bg='info'>
                <Text heading='xxs' weight='semi-bold' color='primary'>
                  {product.dimension.fullSize}
                </Text>
              </Badge>
              <SeasonIcon value={product.season.id} size='xs' />
              <Badge bg='gray-200' pill text='muted'>
                {product.vehicleType.name}
              </Badge>
            </Flex>

            {/* <h5>{t('app.common.description')}</h5> */}
            <p className='mt-2'>
              {product.dimension.description
                ? product.dimension.description
                : t('app.purchase.search_page.detail.no_description')}
            </p>
            <hr />
            <div className='d-grid grid-cols-2 mt-2'>
              <Text weight='semi-bold' color='secondary-gray'>
                <Trans i18nKey='app.common.product.ean' />
              </Text>
              <Text weight='semi-bold' color='secondary'>
                {product.ean}
              </Text>

              {customer.settingTemplate?.generalSetting.tyreIdentifierFields?.includes(
                TYRE_IDENTIFIER_FIELD.WYZ_REFERENCE,
              ) &&
                product.alternativeReference && (
                  <>
                    <Text weight='semi-bold' color='secondary-gray'>
                      <Trans i18nKey='app.common.product.reference' />
                    </Text>
                    <Text weight='semi-bold' color='secondary'>
                      {product.alternativeReference}
                    </Text>
                  </>
                )}
              {customer.settingTemplate?.generalSetting.tyreIdentifierFields?.includes(
                TYRE_IDENTIFIER_FIELD.CONSTRUCTOR_REFERENCE,
              ) &&
                TYRE_IDENTIFIER_FIELD.CONSTRUCTOR_REFERENCE &&
                product.constructorReference && (
                  <>
                    <Text weight='semi-bold' color='secondary-gray'>
                      <Trans i18nKey='app.purchase.search_page.detail.reference_constructor' />
                    </Text>
                    <Text weight='semi-bold' color='secondary'>
                      {product.constructorReference}
                    </Text>
                  </>
                )}

              {customer.settingTemplate?.generalSetting.tyreIdentifierFields?.includes(
                TYRE_IDENTIFIER_FIELD.CAI,
              ) &&
                TYRE_IDENTIFIER_FIELD.CAI && (
                  <>
                    <Text weight='semi-bold' color='secondary-gray'>
                      <Trans i18nKey='app.purchase.search_page.detail.cai' />
                    </Text>
                    <Text weight='semi-bold' color='secondary'>
                      {product.supplierCode}
                    </Text>
                  </>
                )}

              <Text weight='semi-bold' color='secondary-gray'>
                <Trans i18nKey='app.purchase.search_page.detail.dimension' />
              </Text>
              <Text weight='semi-bold' color='secondary'>
                {product.dimension.fullSize}
              </Text>

              <Text weight='semi-bold' color='secondary-gray'>
                <Trans i18nKey='app.common.product.season' />
              </Text>
              <Text weight='semi-bold' color='secondary'>
                {product.season.name}
              </Text>

              <Text weight='semi-bold' color='secondary-gray'>
                <Trans i18nKey='app.common.product.homologation' />
              </Text>
              <Text weight='semi-bold' color='secondary'>
                {product.prodInfo
                  ? product.prodInfo
                  : t('app.purchase.search_page.detail.no_homologation')}
              </Text>

              <Text weight='semi-bold' color='secondary-gray'>
                <Trans i18nKey='app.common.product.seal_run_flat' />
              </Text>
              <Text weight='semi-bold' color='secondary'>
                <Trans
                  i18nKey={`app.common.messages.${
                    product.specification.rftext ||
                    product.specification.seal === 'Y'
                      ? 'yes'
                      : 'no'
                  }`}
                />
              </Text>

              <Text weight='semi-bold' color='secondary-gray'>
                <Trans i18nKey='app.purchase.search_page.detail.specificities' />
              </Text>
              <Text weight='semi-bold' color='secondary'>
                {specificities.length ? specificities.join(', ') : '-'}
              </Text>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal.Container>
  );
};

export default DetailModal;
